import buildUtmCampaign from './buildUtmCampaign'
import buildUtmContent from './buildUtmContent'
import { buildShortSlug, buildFinalUtmUrl } from './buildFinalUtmUrl'
import { buildShortLinkFields } from './buildShortLink'
import { resolveUtmMedium, validateUtmChannelFormat } from './resolveUtmMedium'
import { resolveOwnerLabel } from './resolveOwnerLabel'
import { assertNoPublicOwnerLeak } from './assertNoPublicOwnerLeak'
import buildCleanCampaignKey from './buildCleanCampaignKey'
import resolveCampaignDateKey from './resolveCampaignDateKey'
import { stripDateFromCampaignTitle } from './parseDateFromCampaignTitle'
import { slugifyTitleForUtm } from './slugifyLatin'
import { hasCyrillic } from './transliterateCyrillic'
import getInvalidCombinationMessage from './getInvalidCombinationMessage'
import {
  getMissingPreviewFields,
  hasRequiredForPreview,
  PREVIEW_STATE,
  resolvePreviewState,
} from './utmPreviewFields'
import { UTM_BUILDER_DEFAULTS } from '../../config/utmBuilderDefaults'

export { getMissingPreviewFields, hasRequiredForPreview, PREVIEW_STATE, PREVIEW_REQUIRED_FIELDS } from './utmPreviewFields'

export const MISSING_CAMPAIGN_DATE_WARNING =
  'Дата не вказана. Campaign key буде без дати. Для промо або публікації краще обрати промо або вказати дату.'

const DATE_RECOMMENDED_FORMATS = new Set([
  'ad',
  'story',
  'post',
  'reel',
  'banner_ad',
  'email',
  'broadcast',
  'partner_post',
])

const hasPreviewInput = (values = {}) =>
  Boolean(
    values.title ||
      values.owner ||
      values.destinationUrl ||
      values.channel ||
      values.format ||
      values.direction
  )

const resolveUtmSource = (channel) => channel || ''

const collectTitleWarnings = (title = '') => {
  const warnings = []
  const strippedTitle = stripDateFromCampaignTitle(title)
  const titleSlug = slugifyTitleForUtm(strippedTitle)

  if (!title?.trim()) {
    return warnings
  }

  if (!titleSlug) {
    warnings.push('Використайте латиницю в назві кампанії для коректного utm_campaign і короткого slug.')
  } else if (hasCyrillic(title) && titleSlug.length < 2) {
    warnings.push('Перевірте транслітерацію назви кампанії — slug може бути неповним.')
  }

  return warnings
}

const buildCampaignKeyInput = (values = {}) => ({
  title: values.title,
  relatedPromoStartDate: values.related_promo_start_date,
  campaignDate: values.campaign_date,
})

export const buildCampaignLinkPreview = (values = {}, config = UTM_BUILDER_DEFAULTS) => {
  const missingFields = getMissingPreviewFields(values)

  if (!hasPreviewInput(values)) {
    return {
      previewState: PREVIEW_STATE.EMPTY,
      missingFields,
      ownerLabel: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_content: '',
      short_slug: '',
      short_path: '',
      short_url: '',
      final_utm_url: '',
      invalidCombinationMessage: null,
      ownerLeakDetected: false,
      warnings: [],
      isComplete: false,
      invalidCombination: false,
    }
  }

  const combinationValidation =
    values.channel && values.format
      ? validateUtmChannelFormat({
          channel: values.channel,
          format: values.format,
          config,
        })
      : { valid: true, warnings: [] }

  const isValidCombination = combinationValidation.valid
  const campaignKeyInput = buildCampaignKeyInput(values)
  const canGenerateUtmParams = isValidCombination && Boolean(buildCleanCampaignKey(campaignKeyInput))
  const previewState = resolvePreviewState({ values, isValidCombination, canGenerateUtmParams })
  const titleWarnings = collectTitleWarnings(values.title)

  const warnings = [
    ...(previewState === PREVIEW_STATE.VALID ? combinationValidation.warnings ?? [] : []),
    ...(previewState === PREVIEW_STATE.INCOMPLETE ? titleWarnings : []),
    ...(previewState === PREVIEW_STATE.VALID ? titleWarnings : []),
  ]

  const invalidCombinationMessage =
    previewState === PREVIEW_STATE.INVALID && values.channel && values.format
      ? getInvalidCombinationMessage({
          channel: values.channel,
          format: values.format,
          config,
        })
      : null

  const canShowGeneratedUtm = previewState === PREVIEW_STATE.VALID

  const utm_source = canShowGeneratedUtm && values.channel ? resolveUtmSource(values.channel) : ''
  const utm_medium =
    canShowGeneratedUtm && values.channel && values.format
      ? resolveUtmMedium({ channel: values.channel, format: values.format, config })
      : ''
  const utm_content = canShowGeneratedUtm && values.format ? buildUtmContent({ format: values.format }) : ''
  const utm_campaign = canShowGeneratedUtm && values.title ? buildUtmCampaign(campaignKeyInput) : ''
  const short_slug = canShowGeneratedUtm ? buildShortSlug({ campaignKey: utm_campaign }) : ''
  const { short_path, short_url } = buildShortLinkFields(short_slug)
  const final_utm_url =
    canShowGeneratedUtm && values.destinationUrl && utm_source && utm_medium && utm_campaign && utm_content
      ? buildFinalUtmUrl({
          destinationUrl: values.destinationUrl,
          utmSource: utm_source,
          utmMedium: utm_medium,
          utmCampaign: utm_campaign,
          utmContent: utm_content,
        })
      : ''

  if (canShowGeneratedUtm && DATE_RECOMMENDED_FORMATS.has(values.format) && !resolveCampaignDateKey(values).dateKey) {
    warnings.push(MISSING_CAMPAIGN_DATE_WARNING)
  }

  const ownerLabel = resolveOwnerLabel(values.owner, config.owners)

  const leakCheck = assertNoPublicOwnerLeak({
    owner: values.owner,
    owners: config.owners,
    publicFields: {
      utm_campaign,
      utm_content,
      short_slug,
      short_url,
      final_utm_url,
    },
  })

  if (!leakCheck.ok) {
    console.warn('[buildCampaignLinkPreview] Owner leak detected in public fields:', leakCheck.leakedIn)
  }

  return {
    previewState,
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    short_slug,
    short_path,
    short_url,
    final_utm_url,
    ownerLabel,
    ownerLeakDetected: !leakCheck.ok,
    isComplete: previewState === PREVIEW_STATE.VALID,
    invalidCombination: previewState === PREVIEW_STATE.INVALID,
    invalidCombinationMessage,
    warnings,
    missingFields,
  }
}

export default buildCampaignLinkPreview
